import { DynamicDataInput } from '@/types/automationFields.ts';
import { AutomationStepWithChildren } from '@/types/automationSteps.ts';
import {
  getDataInputOptionValue,
  makeNewInputConfig,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/hooks/useDataInputOptions.ts';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import useRetrieveChildrenDataOutputs from '@/hooks/api/steps/useRetrieveChildrenDataOutputs';
import { useDataInputIconContext } from '@/context/DataInputIconContext/useDataInputIconContext.ts';
import React, { useMemo } from 'react';
import { Option, withIcons } from '@/context/DataInputIconContext/utils.ts';
import CustomSelectOption from '@/components/CustomSelectOption.tsx';
import { Box } from '@mantine/core';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import {
  NO_CHILD_OUTPUTS_DESCRIPTION,
  NO_CHILD_OUTPUTS_PLACEHOLDER,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/constants.ts';
import useRetrieveOutputContracts from '@/hooks/api/steps/useRetrieveOutputContracts.ts';
import DataInputSchemaContracts from '@/pages/Editor/components/SharedFields/DataInputSelector/DataInputSchemaContracts.tsx';
import { buildOutputContractByOptionValue } from '@/pages/Editor/components/SharedFields/DataInputSelector/dataInputSchemaContractUtils.ts';

type props = {
  step: AutomationStepWithChildren;
  label?: string;
  dataInput?: DynamicDataInput;
  onChange: (dataInput?: DynamicDataInput) => void;
  dataInputError?: string;
  required: boolean;
  allowedStepIds?: string[];
};

const SingleChildDataInputSelector = ({
  step,
  label,
  dataInput,
  dataInputError,
  onChange,
  required,
  allowedStepIds,
}: props) => {
  const { outputs: childrenDataOutputs, isPending: loadingChildrenDataOutputs } =
    useRetrieveChildrenDataOutputs(step.step_id);
  const { data: outputContracts } = useRetrieveOutputContracts();
  const selectableChildrenDataOutputs = useMemo(() => {
    if (!allowedStepIds) {
      return childrenDataOutputs;
    }

    const allowedStepIdSet = new Set(allowedStepIds);
    return childrenDataOutputs.filter((output) => allowedStepIdSet.has(output.stepId));
  }, [allowedStepIds, childrenDataOutputs]);

  const selectedValue = dataInput ? getDataInputOptionValue(dataInput.config) : null;

  const { getIconForOptionValue, isLoading: loadingIcons } = useDataInputIconContext();
  const isLoading = loadingChildrenDataOutputs || loadingIcons;

  // Inject icons into all options
  const options = useMemo(() => {
    return withIcons(selectableChildrenDataOutputs, getIconForOptionValue);
  }, [getIconForOptionValue, selectableChildrenDataOutputs]);
  const showEmptyState = !isLoading && options.length === 0 && !selectedValue;

  const selectedOption = useMemo(
    () => options.find((o: Option) => o.value === selectedValue) as any | undefined,
    [options, selectedValue],
  );
  const outputContractByValue = useMemo(() => {
    const childContracts = outputContracts?.children_output_contracts?.[step.step_id] ?? [];
    return buildOutputContractByOptionValue(childContracts);
  }, [outputContracts?.children_output_contracts, step.step_id]);
  const selectedOutputContract = selectedValue
    ? outputContractByValue.get(selectedValue) ?? null
    : null;

  const displayOptions = useMemo(() => {
    if (!selectedValue || selectedOption || !dataInput) {
      return options;
    }

    const lastKnownDisplayName = dataInput.last_known_display_name || dataInput.name;
    return [
      ...options,
      {
        value: selectedValue,
        label: 'Deleted Data Input' + (lastKnownDisplayName ? ` (${lastKnownDisplayName})` : ''),
      },
    ];
  }, [dataInput, options, selectedOption, selectedValue]);

  return (
    <Box pos="relative">
      <CustomLoadingOverlay visible={isLoading} />
      <StyledSelect
        label={label || 'Data Input'}
        placeholder={showEmptyState ? NO_CHILD_OUTPUTS_PLACEHOLDER : 'Select a data input'}
        description={showEmptyState ? NO_CHILD_OUTPUTS_DESCRIPTION : undefined}
        data={displayOptions}
        value={selectedValue}
        onChange={(value, _) => {
          onChange(
            (value === null
              ? undefined
              : makeNewInputConfig(value, selectableChildrenDataOutputs)) as DynamicDataInput,
          );
        }}
        required={required}
        allowDeselect={false}
        clearable={!required}
        error={dataInputError}
        disabled={showEmptyState}
        renderOption={({ option, checked }) => {
          const o = option as any;
          return <CustomSelectOption label={o.label} leftSection={o.leftSection} checked={checked} />;
        }}
        leftSection={selectedOption?.leftSection ?? null}
      />
      <DataInputSchemaContracts
        outputContracts={selectedOutputContract ? [selectedOutputContract] : []}
      />
    </Box>
  );
};

export default SingleChildDataInputSelector;
